/**
 * The narration stream: what VoteMate is doing, and why, while it works.
 *
 * Most of it arrives live on the `event: narration` channel, discriminated by
 * `narration_kind`:
 *
 * - `tool`        — a status line for one tool call, with a `tool_category`.
 * - `transition`  — conversational filler redirected out of the answer body,
 *                   or a system note like the auto-depth escalation.
 * - `thinking`    — Claude's reasoning summary. Current backends no longer
 *                   send it (raw deliberation read wrong on a voter-facing
 *                   surface); kept in the fold logic so an older backend's
 *                   frames still render instead of being dropped.
 *
 * One kind does not arrive live at all:
 *
 * - `context`     — a source the turn read from its system message rather than
 *                   called: the voter's own ballot block. It rides the `end`
 *                   frame as `context_steps` (see `foldContextSteps`), because
 *                   the backend can only tell the block was *used* once the
 *                   answer exists. It renders identically to a `tool` step and
 *                   sorts ahead of them, since reading the ballot is what the
 *                   turn actually did first.
 *
 * They read differently, so they render differently: tool calls as a checklist
 * of discrete steps with an icon each, reasoning as prose. Keeping them as a
 * structured list rather than one concatenated string is what makes that
 * possible, and is what lets the reasoning stay readable after the answer
 * lands instead of being cleared with the spinner.
 */

import { resolveSupportedLanguage } from '@/constants/languages';

/** Tool families the backend groups its ~45 tools into, one icon each. */
export const TOOL_CATEGORIES = [
  'web',
  'news',
  'legislative',
  'ballotpedia',
  'usafacts',
  'candidate',
  'ballot',
  'profile',
  'generic',
] as const;

export type ToolCategory = (typeof TOOL_CATEGORIES)[number];

export type NarrationKind = 'thinking' | 'tool' | 'context' | 'transition';

/** Kinds that render as a discrete step with an icon. */
const STEP_KINDS = new Set<NarrationKind>(['tool', 'context']);

/**
 * True for entries the trail counts as a step.
 *
 * Exported so the disclosure's "· N steps" label and its analytics agree with
 * what the timeline actually draws — counting only `tool` would leave a trail
 * whose visible rows outnumber its own count.
 */
export const isStepEvent = (event: NarrationEvent): boolean => STEP_KINDS.has(event.kind);

export interface NarrationEvent {
  kind: NarrationKind;
  /** Accumulated text. Thinking deltas merge into one entry as they stream. */
  text: string;
  /** Present on `tool` entries; drives the icon. */
  toolCategory?: ToolCategory;
  /**
   * Catalog key under `chat:narration` for a backend-authored line (a tool
   * status, a context step, a system note), so the step renders in the voter's
   * language. `text` is the backend's English rendering and the fallback when
   * this client's catalog lacks the key. Absent on model-written prose, which
   * is already in the answer's language.
   */
  statusKey?: string;
  /** Placeholder values for `statusKey` — a query, a name, a host. */
  statusParams?: Record<string, string>;
}

/** A `narration` SSE frame, as the backend sends it. */
export interface NarrationFrame {
  system_msg?: string;
  narration_kind?: string;
  tool_category?: string;
  status_key?: string;
  status_params?: unknown;
}

/** The string-valued entries of a frame's `status_params`, or `undefined` when
 *  there are none. Anything that is not a plain object of strings is dropped
 *  rather than interpolated — the values land inside voter-facing copy. */
function stringParams(value: unknown): Record<string, string> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const entries = Object.entries(value as Record<string, unknown>).filter(
    (entry): entry is [string, string] => typeof entry[1] === 'string'
  );
  return entries.length ? Object.fromEntries(entries) : undefined;
}

const KNOWN_KINDS = new Set<string>(['thinking', 'tool', 'context', 'transition']);
const KNOWN_CATEGORIES = new Set<string>(TOOL_CATEGORIES);

/** Ceiling on retained entries per turn. Narration is kept after the answer
 *  lands (that is the point of the disclosure), so nothing else trims it, and a
 *  long research turn on a long-lived tab would otherwise grow without bound.
 *  Well above what a real turn produces — a heavy fan-out runs a few dozen
 *  steps — so this is a backstop, not a budget. Oldest entries drop first;
 *  reasoning deltas merge into one entry and so do not count against it. */
const MAX_NARRATION_EVENTS = 250;

/**
 * Fold one narration frame into the running list.
 *
 * Returns a new array; never mutates. Two rules carry the behaviour:
 *
 * 1. **Consecutive thinking deltas merge.** Reasoning streams token by token,
 *    so a naive push would produce one entry per fragment and render as
 *    shredded words. Tool and transition entries are discrete and never merge.
 * 2. **An unlabelled frame is treated as a tool status line.** Older backends
 *    send no `narration_kind`, and before this change every narration frame was
 *    a tool status or a transition — so that is the safe reading, and it keeps a
 *    mixed-version deploy rendering sensibly rather than blank.
 * 3. **A `context` entry is inserted ahead of the tool steps.** The backend can
 *    only confirm the ballot block was used once the answer exists, so those
 *    frames arrive last; appending them would show the trail reading the ballot
 *    *after* the searches it actually preceded. Inserting at the boundary keeps
 *    each group in arrival order and costs no reordering on the render side.
 */
export function foldNarration(events: NarrationEvent[], frame: NarrationFrame): NarrationEvent[] {
  const text = frame.system_msg ?? '';
  if (!text) return events;

  const rawKind = frame.narration_kind ?? '';
  const kind: NarrationKind = KNOWN_KINDS.has(rawKind) ? (rawKind as NarrationKind) : 'tool';

  if (kind === 'thinking') {
    const last = events[events.length - 1];
    if (last?.kind === 'thinking') {
      return [...events.slice(0, -1), { ...last, text: last.text + text }];
    }
    return [...events, { kind, text }];
  }

  const rawCategory = frame.tool_category ?? '';
  const toolCategory: ToolCategory | undefined = !STEP_KINDS.has(kind)
    ? undefined
    : KNOWN_CATEGORIES.has(rawCategory)
      ? (rawCategory as ToolCategory)
      : 'generic';

  const statusKey =
    typeof frame.status_key === 'string' && frame.status_key ? frame.status_key : undefined;
  const statusParams = statusKey ? stringParams(frame.status_params) : undefined;

  const entry: NarrationEvent = {
    kind,
    text: text.trim(),
    ...(toolCategory ? { toolCategory } : {}),
    ...(statusKey ? { statusKey } : {}),
    ...(statusParams ? { statusParams } : {}),
  };

  // Context steps go at the end of the context group rather than the end of the
  // list, so they lead the trail in the order the turn actually ran.
  let next: NarrationEvent[];
  if (kind === 'context') {
    const boundary = events.findIndex((e) => e.kind !== 'context');
    next =
      boundary === -1
        ? [...events, entry]
        : [...events.slice(0, boundary), entry, ...events.slice(boundary)];
  } else {
    next = [...events, entry];
  }

  return next.length > MAX_NARRATION_EVENTS ? next.slice(next.length - MAX_NARRATION_EVENTS) : next;
}

/** One `context_steps` entry on the `end` frame, as the backend sends it. */
export interface ContextStepFrame {
  text?: string;
  /** Catalog key under `chat:narration`, like a live frame's `status_key`. */
  key?: string;
  tool_category?: string;
}

/**
 * Fold the `end` frame's context steps into the trail.
 *
 * Separate from `foldNarration` only in where its input comes from: these
 * describe sources the turn read from its system message rather than called, so
 * the backend cannot confirm they were used until the answer exists and sends
 * them with the turn's other post-hoc facts. Each is folded through the same
 * function as a live frame, so ordering and the entry cap stay in one place.
 *
 * Tolerates a missing or malformed field: older backends omit it entirely, and
 * this runs on every turn's `end`.
 */
export function foldContextSteps(events: NarrationEvent[], steps: unknown): NarrationEvent[] {
  if (!Array.isArray(steps)) return events;
  return steps.reduce<NarrationEvent[]>((acc, step) => {
    const { text, key, tool_category: category } = (step ?? {}) as ContextStepFrame;
    if (!text) return acc;
    return foldNarration(acc, {
      system_msg: text,
      narration_kind: 'context',
      tool_category: category,
      status_key: key,
    });
  }, events);
}

/**
 * True when there is a research trail worth keeping after the answer.
 *
 * Deliberately broader than "the turn produced reasoning": the tool steps ARE
 * the trail. Gating the disclosure on reasoning alone hid it on every turn where
 * Claude searched but emitted no summary — which is every turn on a deployment
 * where `thinking.display` is not honoured, and the control is labelled
 * "· N steps" precisely because the steps carry value on their own.
 *
 * `text` is guarded rather than assumed: this runs during render, so a frame
 * that arrives without one degrades instead of blanking the chat.
 */
export function hasResearchTrail(events: NarrationEvent[] | undefined): boolean {
  return !!events?.some((e) => e.kind !== 'transition' && (e.text ?? '').trim().length > 0);
}

/** True when the trail has at least two research steps worth de-cluttering. */
export function shouldShowReasoningDisclosure(events: NarrationEvent[] | undefined): boolean {
  return (events ?? []).filter((e) => e.kind !== 'transition').filter(isStepEvent).length > 1;
}

/**
 * Step count above which the live trail collapses to its current step.
 *
 * A deep research turn can run a few dozen steps, and while the list grows it
 * pushes everything else in the chat off the screen. Eight rows still fit on a
 * phone alongside the voter's question; past that the latest step carries the
 * signal and the full list stays one tap away.
 */
export const LIVE_TRAIL_COLLAPSE_AFTER = 8;

/** Number of entries the trail draws as steps. */
export const countSteps = (events: NarrationEvent[]): number => events.filter(isStepEvent).length;

/** True once the live trail has grown long enough to collapse. */
export function shouldCollapseLiveTrail(events: NarrationEvent[]): boolean {
  return countSteps(events) > LIVE_TRAIL_COLLAPSE_AFTER;
}

/**
 * The entry the collapsed live trail shows: the newest step or notice, along
 * with its index so the caller can replay its entrance on each new one.
 * Reasoning is skipped because it streams as a growing paragraph rather than
 * marking progress.
 */
export function currentLiveEntry(
  events: NarrationEvent[]
): { event: NarrationEvent; index: number } | undefined {
  for (let i = events.length - 1; i >= 0; i--) {
    const event = events[i];
    if (event.kind !== 'thinking' && (event.text ?? '').trim()) return { event, index: i };
  }
  return undefined;
}

/**
 * The shape of an i18next `t` this module needs. Structural rather than
 * i18next's own type so the fold logic stays free of platform imports and the
 * two clients keep one copy of it.
 */
export type NarrationTranslate = (key: string, options: Record<string, unknown>) => string;

/**
 * The line to show for a narration entry, in the language `t` speaks.
 *
 * A backend-authored line carries a catalog key, so it renders from the
 * catalog with its parameters spliced in; the backend's English text is the
 * `defaultValue`, so a key this client does not know yet (a tool added on the
 * server first) degrades to the same English line it showed before keys
 * existed rather than to a raw key. Model-written prose has no key and is
 * shown as written — it is already in the answer's language.
 */
export function narrationEventText(event: NarrationEvent, t: NarrationTranslate): string {
  if (!event.statusKey) return event.text;
  return t(`narration.${event.statusKey}`, {
    ...(event.statusParams ?? {}),
    defaultValue: event.text,
  });
}

/**
 * The language a turn's trail should render in, or `undefined` to follow the UI.
 *
 * The backend writes the answer in the language of the voter's message and
 * falls back to the UI language (`reply_language` on its frames). When the two
 * agree the trail simply follows the UI, so it switches with the rest of the
 * screen when the voter changes language later — the same way the disclosure's
 * own label does. When the voter asked in another language, the answer beside
 * the trail is in that language, so the trail pins to it and the two read as
 * one; that pin outlives a later UI switch for the same reason the answer's
 * text does.
 */
export function trailLanguageFor(
  replyLanguage: unknown,
  uiLanguage: string | undefined
): string | undefined {
  const reply = resolveSupportedLanguage(typeof replyLanguage === 'string' ? replyLanguage : null);
  if (!reply) return undefined;
  return reply === resolveSupportedLanguage(uiLanguage ?? null) ? undefined : reply;
}
